/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/admin/DevSettingsPanel.tsx
 * Release: v1.0.0 - Pannello Impostazioni Sviluppo
 * Descrizione: UI Toggle per switch mock/live data e altre impostazioni dev
 *              Accessibile solo da superadmin
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Settings, Database, Eye, EyeOff, Clock, Terminal,
  ToggleLeft, ToggleRight, RefreshCw, Beaker, Zap, AlertTriangle
} from 'lucide-react';
import { useDevFeatures } from '../../config/devFeatures';

interface DevSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ToggleRowProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  value: boolean;
  onChange: (value: boolean) => void;
  warning?: string;
}

const ToggleRow: React.FC<ToggleRowProps> = ({
  icon,
  label,
  description,
  value,
  onChange,
  warning
}) => (
  <div className="flex items-start justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700">
    <div className="flex items-start gap-3">
      <div className="p-2 bg-slate-700 rounded-lg">
        {icon}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-white">{label}</span>
          {warning && value && (
            <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">
              <AlertTriangle className="w-3 h-3" />
              {warning}
            </div>
          )}
        </div>
        <p className="text-sm text-slate-400 mt-0.5">{description}</p>
      </div>
    </div>
    <button
      onClick={() => onChange(!value)}
      className={`
        flex items-center justify-center w-14 h-8 rounded-full transition-colors
        ${value
          ? 'bg-green-600 hover:bg-green-500'
          : 'bg-slate-600 hover:bg-slate-500'
        }
      `}
    >
      {value ? (
        <ToggleRight className="w-6 h-6 text-white" />
      ) : (
        <ToggleLeft className="w-6 h-6 text-white" />
      )}
    </button>
  </div>
);

interface SliderRowProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  unit: string;
}

const SliderRow: React.FC<SliderRowProps> = ({
  icon,
  label,
  description,
  value,
  onChange,
  min,
  max,
  step,
  unit
}) => (
  <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
    <div className="flex items-start gap-3 mb-3">
      <div className="p-2 bg-slate-700 rounded-lg">
        {icon}
      </div>
      <div className="flex-1">
        <span className="font-medium text-white">{label}</span>
        <p className="text-sm text-slate-400 mt-0.5">{description}</p>
      </div>
      <div className="text-lg font-mono font-bold text-blue-400">
        {value}{unit}
      </div>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer
        [&::-webkit-slider-thumb]:appearance-none
        [&::-webkit-slider-thumb]:w-4
        [&::-webkit-slider-thumb]:h-4
        [&::-webkit-slider-thumb]:bg-blue-500
        [&::-webkit-slider-thumb]:rounded-full
        [&::-webkit-slider-thumb]:cursor-pointer
        [&::-webkit-slider-thumb]:hover:bg-blue-400
      "
    />
    <div className="flex justify-between text-xs text-slate-500 mt-1">
      <span>{min}{unit}</span>
      <span>{max}{unit}</span>
    </div>
  </div>
);

export const DevSettingsPanel: React.FC<DevSettingsPanelProps> = ({ isOpen, onClose }) => {
  const { features, setFeature, resetAll } = useDevFeatures();

  const handleResetAndReload = () => {
    resetAll();
    window.location.reload();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-900/50 to-slate-800 px-6 py-4 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Beaker className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Impostazioni Sviluppo</h2>
                  <p className="text-sm text-slate-400">Toggle funzionalità dev (superadmin)</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-slate-400" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Data Source Section */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Database className="w-4 h-4" />
                Sorgente Dati
              </h3>

              <ToggleRow
                icon={<Database className="w-5 h-5 text-amber-400" />}
                label="Usa Mock Data"
                description="Quando attivo, i componenti usano dati simulati invece delle API reali"
                value={features.useMockData}
                onChange={(v) => setFeature('useMockData', v)}
                warning="Mock attivo"
              />

              <div className="mt-4 p-3 bg-slate-800/80 rounded-lg border border-slate-600">
                <div className="flex items-center gap-2 text-sm">
                  <Terminal className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-400">Console:</span>
                  <code className="px-2 py-0.5 bg-slate-700 rounded text-xs font-mono text-blue-400">
                    devFeatures.{features.useMockData ? 'useLive()' : 'useMock()'}
                  </code>
                </div>
              </div>
            </div>

            {/* UI Indicators Section */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Indicatori UI
              </h3>

              <ToggleRow
                icon={features.showDataSourceIndicator
                  ? <Eye className="w-5 h-5 text-green-400" />
                  : <EyeOff className="w-5 h-5 text-slate-400" />
                }
                label="Mostra Badge MOCK/LIVE"
                description="Visualizza un badge nelle UI che indica se i dati sono mock o reali"
                value={features.showDataSourceIndicator}
                onChange={(v) => setFeature('showDataSourceIndicator', v)}
              />
            </div>

            {/* Performance Section */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Performance
              </h3>

              <SliderRow
                icon={<Clock className="w-5 h-5 text-blue-400" />}
                label="Latenza Simulata"
                description="Simula ritardo di rete per mock data (utile per testare loading states)"
                value={features.mockNetworkDelay}
                onChange={(v) => setFeature('mockNetworkDelay', v)}
                min={0}
                max={3000}
                step={100}
                unit="ms"
              />
            </div>

            {/* Debug Section */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Terminal className="w-4 h-4" />
                Debug
              </h3>

              <ToggleRow
                icon={<Terminal className="w-5 h-5 text-cyan-400" />}
                label="Log API Verbose"
                description="Mostra log dettagliati delle chiamate API nella console"
                value={features.verboseApiLogging}
                onChange={(v) => setFeature('verboseApiLogging', v)}
              />

              <div className="mt-4">
                <ToggleRow
                  icon={<Settings className="w-5 h-5 text-purple-400" />}
                  label="DevSimulator"
                  description="Abilita il pulsante di simulazione rapida nei form (riempimento dati test)"
                  value={features.devSimulatorEnabled}
                  onChange={(v) => setFeature('devSimulatorEnabled', v)}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-700 bg-slate-800/50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-400">
                Le modifiche sono salvate automaticamente in localStorage
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleResetAndReload}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Reset e Ricarica
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DevSettingsPanel;
